import type { DocumentRequirement } from '@/types';
import type { FilterableFarmProfile } from '@/types/farm-profile';
import { evaluateCondition, findFailingConditionReason } from '@shared/farm-filter';

export interface FilterResult<T> {
  visible: T[];
  hidden: Array<{ requirement: T; reason: string }>;
}

/**
 * Filter document requirements against a farm profile.
 *
 * Algorithm:
 * 1. If `profile` is null → all requirements are visible (no profile = no filter).
 * 2. For each requirement, evaluate its own `filterRule` with AND logic. A null,
 *    empty or malformed rule never hides; nothing is inherited (AG-249).
 *    The first failing condition's `reason` is surfaced in the hidden list.
 *
 * @param requirements - The full list of document requirements to filter.
 * @param profile - The current farm profile (full, or the redacted shape the
 *   public share endpoint returns), or null if not yet onboarded.
 * @returns FilterResult with `visible` requirements and `hidden` entries with reasons.
 */
export function applyFarmProfileFilter<T extends DocumentRequirement>(
  requirements: T[],
  profile: FilterableFarmProfile | null,
): FilterResult<T> {
  // No profile → show everything (user hasn't onboarded; we don't hide anything)
  if (profile === null) {
    return { visible: requirements, hidden: [] };
  }

  const visible: T[] = [];
  const hidden: Array<{ requirement: T; reason: string }> = [];

  for (const req of requirements) {
    // AND logic; the first failing condition supplies the reason. A missing or
    // malformed rule evaluates to "no filter" inside the shared evaluator.
    const failingReason = findFailingConditionReason(req.filterRule, profile);

    if (failingReason === null) {
      visible.push(req);
    } else {
      hidden.push({ requirement: req, reason: failingReason });
    }
  }

  return { visible, hidden };
}

export { evaluateCondition };
